import React, { useRef } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import Skeleton from 'wsm-skeleton';
import useFragmentLoader from '../hooks/useFragmentLoader';
import { resolveFragmentPresentation } from '../utils/fragmentPresentation';

/**
 * FragmentContent — shared fragment renderer for the mega menu and the drawer.
 *
 * Owns the behavior that is genuinely identical between both call sites:
 * lazy-fetching the fragment via useFragmentLoader, showing a skeleton while
 * loading, injecting sanitized HTML on success, and collapsing silently on
 * error. The two things that legitimately differ between the mega menu and the
 * drawer are injected rather than hard-coded:
 *
 *   - `root`              — the IntersectionObserver root (viewport vs. a real
 *                           scroll container), made explicit at the call site.
 *   - `classNameResolver` — the wrapper class-list policy, resolved from the
 *                           current loader state by each wrapper.
 *
 * @param {object} props
 * @param {string} [props.url] - fragment endpoint URL; when absent nothing renders
 * @param {React.RefObject<HTMLElement> | null} [props.root] - IntersectionObserver
 *   root; null/omitted means viewport-relative
 * @param {(state: { status: string, html: string|null }) => string} props.classNameResolver
 *   - resolves the wrapper element's full className from the current loader state
 * @param {string} [props.skeletonHeight] - height passed to the loading Skeleton
 * @param {boolean} [props.reinitOnReshow=true] - forwarded to useFragmentLoader;
 *   when false, the fragment is not re-initialized each time it becomes visible
 *   again (the mega menu opts out to avoid leaking on every reopen). Defaults to
 *   true so the drawer keeps its remount-driven re-init behavior.
 * @param {(event: React.MouseEvent) => void} [props.onClick] - attached to the
 *   existing slot element rather than a new wrapper, because the host SCSS keys
 *   off the exact `…__fragment > div > div.ddc-page` chain (see _fragment.scss)
 *   and an extra element would break it. Used for click-delegated instrumentation
 *   over injected fragment markup this widget does not author.
 * @param {string} [props.recVehiclesContext] - when set, stamps
 *   data-rec-vehicles-context on the fragment container so an embedded
 *   ws-rec-vehicles instance can detect the surface it renders in (see
 *   DrawerNavFragment). Omitted by the mega menu, so mega-menu rec-vehicles
 *   keeps its default layout.
 * @param {boolean} [props.headless=false] - true when this mount exists only
 *   to fetch and hydrate a widget that renders its own hidden UI elsewhere
 *   (e.g. a modal portaled to document.body), never to show inline content.
 *   See DrawerNavFragment's `headless` prop for the motivating case: a
 *   fragment mounted eagerly in the persistent header row, rather than
 *   lazily inside an opened drilldown panel. When true, this suppresses:
 *     - the loading skeleton (nothing should visibly flash in a surface
 *       nobody is meant to look at yet), and
 *     - the aria-busy/loaded-status announcements (there's no perceivable
 *       content to tell a screen reader about).
 */
const FragmentContent = ({
	url = '',
	root = null,
	classNameResolver,
	skeletonHeight = '12rem',
	reinitOnReshow = true,
	onClick = undefined,
	recVehiclesContext = undefined,
	headless = false
}) => {
	const slotRef = useRef(null);
	// Pass url||'' to keep hooks unconditional; useFragmentLoader bails via its
	// !el guard when url is absent.
	const { status, html, renderKey } = useFragmentLoader(
		url || '',
		slotRef,
		root,
		{
			reinitOnReshow
		}
	);

	// No URL — nothing to fetch; render nothing (hooks called above to satisfy
	// the Rules of Hooks).
	if (!url) return null;

	const { isLoaded, isPending, isVisible } = resolveFragmentPresentation(
		status,
		headless
	);

	let content;
	if (isLoaded) {
		// key={renderKey}: forces this DOM to be recreated from scratch whenever
		// useFragmentLoader needs the fragment's script to re-run (see its
		// renderKey docs) — the script only executes its init logic when
		// inserted against markup it hasn't already mutated.
		content = (
			// eslint-disable-next-line react/no-danger
			<div key={renderKey} dangerouslySetInnerHTML={{ __html: html }} />
		);
	} else if (isVisible && isPending) {
		// idle or loading, not headless — show skeleton
		content = <Skeleton height={skeletonHeight} width="100%" />;
	} else {
		// error, or idle/loading while headless — nothing visible to render
		content = null;
	}

	return (
		<>
			{/* The slot is a passive container, not a control: the click handler only
			   observes activations of the real links/buttons inside the injected
			   markup, which already carry their own keyboard semantics (Enter/Space on
			   those elements dispatches a click, so delegation sees keyboard users
			   too). Adding a key handler or a role here would announce an interactive
			   element that does not exist. */}
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
				ref={slotRef}
				className={classNameResolver({ status, html })}
				data-testid="fragment-slot"
				data-rec-vehicles-context={recVehiclesContext}
				aria-busy={isVisible ? isPending : undefined}
				onClick={onClick}
			>
				{content}
			</div>
			{isVisible && (
				<span className="sr-only" aria-live="polite">
					{isLoaded ? 'Content loaded' : ''}
				</span>
			)}
		</>
	);
};

FragmentContent.propTypes = {
	url: PropTypes.string,
	root: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	classNameResolver: PropTypes.func.isRequired,
	skeletonHeight: PropTypes.string,
	reinitOnReshow: PropTypes.bool,
	onClick: PropTypes.func,
	recVehiclesContext: PropTypes.string,
	headless: PropTypes.bool
};

export default FragmentContent;
